import React from "react";
import { Button, cn } from "@ndd/ui";
import type { RdHoursWeekdayName } from "../../../../../shared/api-types";
import {
  RD_HOURS_COPY,
  isTerminalSaveError,
  saveErrorCopy,
  type RdHoursErrorKind,
} from "../../../lib/rd-hours-client";
import type { RdHoursMode } from "../../../lib/rd-hours-draft";
import { isEditingLocked } from "./read-failure";

export interface SaveBarProps {
  mode: RdHoursMode;
  /** Days the client break rule blocks, in week order. */
  blockedDays: readonly RdHoursWeekdayName[];
  /** Why those days cannot save: names the day, and the stretch when a break exists. */
  blockedMessage: string;
  dirty: boolean;
  /** A save is in flight. */
  saving: boolean;
  /** A save's answer or the latest hours are being read; Save waits. */
  resolving: boolean;
  /** The last save's refusal, until the next edit. */
  error: RdHoursErrorKind | null;
  /** The last save succeeded and nothing has changed since. */
  saved: boolean;
  onSave: () => void;
  onDiscard: () => void;
  onLoadLatest: () => void;
}

export function saveLabel(mode: RdHoursMode, saving: boolean): string {
  if (saving) return "Saving…";

  return mode === "week" ? "Save this week" : "Save weekly hours";
}

export function saveDisabled(props: SaveBarProps): boolean {
  const blocked = props.blockedDays.length > 0 || !props.dirty;
  const busy = isEditingLocked(props.saving, props.resolving);
  const terminal = isTerminalSaveError(props.error);

  return blocked || busy || terminal;
}

/** Disabled reads as the empty track, not a faded olive pill (the canvas). */
const SAVE_DISABLED_LOOK =
  "disabled:bg-secondary disabled:text-muted-foreground disabled:opacity-100 disabled:shadow-none";

/** The line under Save: why it is blocked, unsaved changes, or Saved. */
export function saveNote(
  props: Pick<SaveBarProps, "blockedDays" | "blockedMessage" | "dirty" | "saved">
): { text: string; danger: boolean } {
  if (props.blockedDays.length > 0) {
    return { text: props.blockedMessage, danger: true };
  }

  if (props.dirty) return { text: "Unsaved changes", danger: false };

  return { text: props.saved ? RD_HOURS_COPY.saved : "", danger: false };
}

function ErrorAlert({
  error,
  onLoadLatest,
}: {
  error: RdHoursErrorKind;
  onLoadLatest: () => void;
}) {
  return (
    <div role="alert" className="flex flex-col items-start gap-2 text-body text-danger">
      <span>{saveErrorCopy(error)}</span>
      {error.kind === "version_conflict" ? (
        <Button type="button" variant="utility" size="compact" onClick={onLoadLatest}>
          {RD_HOURS_COPY.loadLatest}
        </Button>
      ) : null}
    </div>
  );
}

/**
 * The note under Save, a polite status region. After a save the editor
 * remounts with "Saved" already in it, and text present when a region mounts
 * is not read out, so "Saved" fills in just after mount.
 */
function SaveNote({
  text,
  danger,
  saved,
}: {
  text: string;
  danger: boolean;
  saved: boolean;
}) {
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => setMounted(true), []);

  return (
    <span
      role="status"
      className={cn(
        "min-h-4 text-body",
        danger ? "text-danger" : "text-muted-foreground"
      )}
    >
      {saved && !mounted ? "" : text}
    </span>
  );
}

export function SaveBar(props: SaveBarProps) {
  const note = saveNote(props);
  // A 422 reads as the blocked line already; the server's own words show only
  // when the client rule had not caught it.
  const showAlert =
    props.error !== null &&
    !(props.error.kind === "break_required" && props.blockedDays.length > 0);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2.5">
        <Button
          type="button"
          variant="press"
          shape="pill"
          className={cn("h-[38px] px-[18px]", SAVE_DISABLED_LOOK)}
          disabled={saveDisabled(props)}
          onClick={props.onSave}
        >
          {saveLabel(props.mode, props.saving)}
        </Button>
        <Button
          type="button"
          variant="utility"
          className="h-9 rounded-md px-3.5"
          disabled={!props.dirty || isEditingLocked(props.saving, props.resolving)}
          onClick={props.onDiscard}
        >
          Discard
        </Button>
      </div>
      {showAlert && props.error !== null ? (
        <ErrorAlert error={props.error} onLoadLatest={props.onLoadLatest} />
      ) : null}
      <SaveNote
        text={note.text}
        danger={note.danger}
        saved={note.text === RD_HOURS_COPY.saved}
      />
    </div>
  );
}
